import { MonitorPlay, Wrench } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import type { Installation } from '../../types'
import { useStaffLookup } from '../../ui'
import { ProgressBar } from '../ProjectBits'
import { RowMenu } from '../RowMenu'
import { InstallationKindBadge, InstallationStatusBadge } from '../StatusBadges'

type Props = {
  rows: Installation[]
  selectedId?: string | null
  onSelect?: (id: string) => void
  canWrite: boolean
  onEdit: (i: Installation) => void
  onDelete: (i: Installation) => void
}

function KindIcon({ kind }: { kind: Installation['kind'] }) {
  const Icon = kind === 'installation' ? Wrench : MonitorPlay
  return (
    <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', kind === 'installation' ? 'bg-success/12 text-success' : 'bg-info/12 text-info')}>
      <Icon className="size-4" aria-hidden />
    </span>
  )
}

export function InstallationTable({ rows, selectedId, onSelect, canWrite, onEdit, onDelete }: Props) {
  const { t } = useTranslation()
  const { nameOf, byId } = useStaffLookup()

  const customer = (i: Installation) => {
    const inner = (
      <>
        <span className="block truncate text-sm font-medium">{i.customer}</span>
        <span className="block truncate text-xs text-muted-foreground">{i.city}</span>
      </>
    )
    return onSelect ? (
      <button type="button" onClick={() => onSelect(i.id)} className="min-w-0 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-pressed={selectedId === i.id}>
        {inner}
      </button>
    ) : (
      <div className="min-w-0">{inner}</div>
    )
  }
  const owner = (i: Installation) =>
    i.ownerId ? (
      <span className="flex min-w-0 items-center gap-2">
        <Avatar name={nameOf(i.ownerId)} className="size-7 [&>span]:text-[10px]" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm">{nameOf(i.ownerId)}</span>
          <span className="block truncate text-[11px] text-muted-foreground">{byId.get(i.ownerId)?.position}</span>
        </span>
      </span>
    ) : (
      <span className="text-sm text-muted-foreground">{t('projects.work.unassigned')}</span>
    )
  const plan = (i: Installation) => (i.startDate === i.targetDate ? fmtDate(i.startDate) : `${fmtDate(i.startDate)} – ${fmtDate(i.targetDate)}`)
  const menu = (i: Installation) => (canWrite ? <RowMenu label={i.customer} onEdit={() => onEdit(i)} onDelete={() => onDelete(i)} /> : null)
  const rowClass = (i: Installation) => cn(selectedId === i.id && 'bg-primary/[0.05] shadow-[inset_3px_0_0_var(--color-primary)]')

  return (
    <div className="@container overflow-hidden rounded-xl border">
      <ul className="divide-y @3xl:hidden">
        {rows.map((i) => (
          <li key={i.id} className={cn('min-w-0 space-y-3 p-3', rowClass(i))}>
            <div className="flex items-start gap-3">
              <KindIcon kind={i.kind} />
              <div className="min-w-0 flex-1">{customer(i)}</div>
              {menu(i)}
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-2 sm:pl-12">
              <InstallationKindBadge kind={i.kind} />
              <InstallationStatusBadge status={i.status} />
              <span className="min-w-0 break-words text-xs tabular-nums text-muted-foreground">{plan(i)}</span>
            </div>
            <div className="min-w-0 space-y-2 sm:pl-12">
              {owner(i)}
              <ProgressBar value={i.progress} label={t('projects.stats.progress')} />
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden @3xl:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              <TH>{t('projects.install.columns.customer')}</TH>
              <TH>{t('projects.install.columns.kind')}</TH>
              <TH className="hidden @5xl:table-cell">{t('projects.install.columns.plan')}</TH>
              <TH className="hidden @4xl:table-cell">{t('projects.install.columns.owner')}</TH>
              <TH>{t('projects.install.columns.target')}</TH>
              <TH className="w-40">{t('projects.stats.progress')}</TH>
              <TH>{t('projects.install.columns.status')}</TH>
              {canWrite ? <TH className="w-12" aria-label={t('projects.work.columns.actions')} /> : null}
            </TR>
          </THead>
          <TBody>
            {rows.map((i) => (
              <TR key={i.id} className={rowClass(i)}>
                <TD>
                  <div className="flex items-center gap-3">
                    <KindIcon kind={i.kind} />
                    {customer(i)}
                  </div>
                </TD>
                <TD>
                  <InstallationKindBadge kind={i.kind} />
                </TD>
                <TD className="hidden whitespace-nowrap text-sm tabular-nums @5xl:table-cell">{plan(i)}</TD>
                <TD className="hidden @4xl:table-cell">{owner(i)}</TD>
                <TD className="whitespace-nowrap text-sm tabular-nums">{fmtDate(i.targetDate)}</TD>
                <TD>
                  <ProgressBar value={i.progress} label={t('projects.stats.progress')} />
                </TD>
                <TD>
                  <InstallationStatusBadge status={i.status} />
                </TD>
                {canWrite ? <TD className="text-right">{menu(i)}</TD> : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  )
}
